import type { Website } from 'types';
import { type FormEvent, useState } from 'react';
import { useWebsites } from '@/state/useWebsites';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, X } from 'lucide-react';
import { cn } from 'helpers/cn';
import { FOCUS_RING } from '../../ConsolePanel';

type ServerContainerUrlProps = {
  website: Website;
  /** A product tour anchor for the cell. */
  tourAnchor?: string;
};

export const ServerContainerUrl = (props: ServerContainerUrlProps) => {
  const { website, tourAnchor } = props;
  const [newServerContainerUrl, setNewServerContainerUrl] = useState<string | null>(null);
  const { setServerContainerUrl } = useWebsites();

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    await setServerContainerUrl(website.id, newServerContainerUrl!);
    setTimeout(() => setNewServerContainerUrl(null), 400);
  };

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'Escape') {
      setNewServerContainerUrl(null);
    }
  };

  return (
    <div data-tour={tourAnchor}>
      {newServerContainerUrl !== null ? (
        <form onSubmit={onSubmit} className="flex items-center gap-2">
          <Input
            value={newServerContainerUrl}
            onChange={(ev) => setNewServerContainerUrl(ev.target.value)}
            onKeyDown={handleKeyDown}
            className="border-line bg-field text-foreground h-8 rounded-lg text-body"
            autoFocus
          />
          <Button
            size="sm"
            variant="ghost"
            type="submit"
            aria-label="Save server container URL"
            title="Save server container URL"
            className="text-dim hover:text-ok hover:bg-hover h-7 w-7 rounded-lg p-0"
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            type="button"
            onClick={() => setNewServerContainerUrl(null)}
            aria-label="Cancel editing server container URL"
            title="Cancel editing server container URL"
            className="text-dim hover:text-foreground hover:bg-hover h-7 w-7 rounded-lg p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </form>
      ) : (
        /* Set reads as content, unset reads as an invitation — hence dim, not accent. */
        <button
          type="button"
          onClick={() => setNewServerContainerUrl(website.serverContainerUrl || '')}
          // The truncated URL is the whole content of this cell, so without a title
          // the only way to read a long one was to click into edit mode.
          title={
            website.serverContainerUrl ? `${website.serverContainerUrl} — click to edit` : 'Set a server container URL'
          }
          className={cn(
            'max-w-full min-h-7 truncate rounded-sm text-meta hover:underline',
            website.serverContainerUrl ? 'text-foreground' : 'micro-label text-dim',
            FOCUS_RING,
          )}
        >
          {website.serverContainerUrl || 'edit'}
        </button>
      )}
    </div>
  );
};
